import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { MapPin, FileText, Wrench, AlertTriangle, SearchCheck } from "lucide-react";

const MACHINE_IMG =
  "https://images.unsplash.com/photo-1579803165101-cfc62dfdbe45?crop=entropy&cs=srgb&fm=jpg&q=85&w=800";
const GALLERY = [
  "https://images.unsplash.com/photo-1777464375799-768774f30542?crop=entropy&cs=srgb&fm=jpg&q=85&w=300",
  "https://images.unsplash.com/photo-1764185800646-f75f7e16e465?crop=entropy&cs=srgb&fm=jpg&q=85&w=300",
  "https://images.unsplash.com/photo-1579803165101-cfc62dfdbe45?crop=entropy&cs=srgb&fm=jpg&q=85&w=300",
];

const TABS = [
  { id: "resumen", label: "Resumen", testid: "passport-tab-resumen" },
  { id: "documentos", label: "Documentos", testid: "passport-tab-documentos" },
  { id: "historial", label: "Historial", testid: "passport-tab-historial" },
  { id: "fotos", label: "Fotos", testid: "passport-tab-fotos" },
];

function TabContent({ tab }) {
  if (tab === "resumen") {
    return (
      <div className="grid grid-cols-2 gap-x-4 gap-y-3 text-left">
        {[
          ["Fabricante", "ENGEL"],
          ["Modelo", "victory 220"],
          ["Número de serie", "220-45891"],
          ["Año", "2019"],
          ["Ubicación", "Nave 1 · Inyección"],
          ["Estado", "Operativa"],
        ].map(([k, v]) => (
          <div key={k}>
            <p className="text-[10px] uppercase tracking-widest text-zinc-400">{k}</p>
            <p className="text-sm text-zinc-900 mt-0.5 font-medium">{v}</p>
          </div>
        ))}
      </div>
    );
  }
  if (tab === "documentos") {
    return (
      <div className="space-y-2 text-left">
        {[
          ["Manual de usuario.pdf", "Manual"],
          ["Esquema eléctrico.pdf", "Esquema eléctrico"],
          ["Ficha técnica.pdf", "Ficha técnica"],
        ].map(([name, type]) => (
          <div key={name} className="flex items-center gap-3 rounded-xl border border-zinc-200/80 bg-white px-3 py-2.5">
            <span className="h-8 w-8 rounded-lg bg-zinc-100 flex items-center justify-center text-zinc-500 shrink-0">
              <FileText size={14} />
            </span>
            <div className="min-w-0">
              <p className="text-sm font-medium truncate">{name}</p>
              <p className="text-xs text-zinc-400">{type}</p>
            </div>
          </div>
        ))}
      </div>
    );
  }
  if (tab === "historial") {
    const items = [
      { date: "14 agosto 2026", type: "Mantenimiento", icon: Wrench, cls: "bg-zinc-100 text-zinc-600", text: "Cambio de filtros del circuito hidráulico." },
      { date: "2 junio 2026", type: "Avería", icon: AlertTriangle, cls: "bg-rose-50 text-rose-500", text: "Fallo en sensor de cierre. Sensor sustituido." },
      { date: "18 marzo 2026", type: "Inspección", icon: SearchCheck, cls: "bg-emerald-50 text-emerald-600", text: "Revisión general realizada." },
    ];
    return (
      <div className="space-y-4 text-left">
        {items.map((it) => (
          <div key={it.date} className="flex gap-3">
            <span className={`h-8 w-8 rounded-full flex items-center justify-center shrink-0 ${it.cls}`}>
              <it.icon size={14} />
            </span>
            <div>
              <p className="text-[11px] text-zinc-400">{it.date} · {it.type}</p>
              <p className="text-sm text-zinc-700 mt-0.5">{it.text}</p>
            </div>
          </div>
        ))}
      </div>
    );
  }
  return (
    <div className="grid grid-cols-3 gap-2">
      {GALLERY.map((src, i) => (
        <img key={i} src={src} alt={`Foto de la máquina ${i + 1}`} className="aspect-square w-full rounded-xl object-cover" loading="lazy" />
      ))}
    </div>
  );
}

export default function MachineMockup({ testid = "hero-passport-mockup", compact = false, testidPrefix = "" }) {
  const [tab, setTab] = useState("resumen");

  return (
    <div
      data-testid={testid}
      className="bg-white rounded-3xl border border-zinc-200/90 shadow-[0_24px_70px_-20px_rgba(0,0,0,0.18)] overflow-hidden"
    >
      <div className="relative">
        <img src={MACHINE_IMG} alt="Inyectora 01" className={`w-full object-cover ${compact ? "h-28" : "h-44 sm:h-52"}`} loading="lazy" />
        <span className="absolute top-3 left-3 inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-[11px] font-medium bg-white/85 backdrop-blur-md text-emerald-700">
          <span className="h-1.5 w-1.5 rounded-full bg-emerald-500" />
          Operativa
        </span>
      </div>
      <div className={`${compact ? "p-4" : "p-5 sm:p-6"}`}>
        <div className="flex items-start justify-between gap-3">
          <div>
            <h3 className={`font-heading font-bold tracking-tight ${compact ? "text-lg" : "text-xl sm:text-2xl"}`}>INYECTORA 01</h3>
            <p className="text-sm text-zinc-500 mt-0.5">ENGEL · victory 220</p>
          </div>
          <div className="text-right text-xs text-zinc-400 space-y-1 shrink-0">
            <p>S/N: 220-45891</p>
            <p className="flex items-center gap-1 justify-end"><MapPin size={11} /> Nave 1 · Inyección</p>
          </div>
        </div>

        <div className="mt-4 bg-zinc-100 rounded-full p-1 grid grid-cols-4">
          {TABS.map((t) => (
            <button
              key={t.id}
              onClick={() => setTab(t.id)}
              data-testid={`${testidPrefix}${t.testid}`}
              className={`rounded-full py-1.5 text-xs sm:text-sm font-medium transition-colors duration-200 ${
                tab === t.id ? "bg-white shadow-sm text-zinc-950" : "text-zinc-500 hover:text-zinc-800"
              }`}
            >
              {t.label}
            </button>
          ))}
        </div>

        <AnimatePresence mode="wait">
          <motion.div
            key={tab}
            initial={{ opacity: 0, y: 6 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -6 }}
            transition={{ duration: 0.18 }}
            className={`${compact ? "mt-3" : "mt-5"} min-h-[150px]`}
          >
            <TabContent tab={tab} />
          </motion.div>
        </AnimatePresence>
      </div>
    </div>
  );
}
